import { useEffect, useMemo, useState } from 'react';
import { Plus, Timer, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { Chip } from '@/Components/ui/Badge';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { TableActions } from '@/Components/ui/TableActions';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import apontamentoService, {
  type ApontamentoConsumo,
  type ApontamentoProducao,
} from '@/services/apontamento';
import ordemProducaoService, { type OrdemProducao, type OrdemProducaoOperacao } from '@/services/ordemProducao';
import produtoService, { type Produto } from '@/services/produto';
import loteService, { type Lote } from '@/services/lote';
import { getUuidCandidate, isUuid } from '@/utils/uuid';

const TIPOS_PRODUTO_ACABADO = ['PA', '04'];

function formatNumber(v: number | string | null | undefined) {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function formatDateTime(v?: string | null) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('pt-BR');
}

const FORM_VAZIO = {
  op: '',
  operacao: '',
  produto: '',
  lote: '',
  quantidade: 0,
  quantidade_refugo: 0,
  funcionario: '',
  responsavel: '',
  tempo_real_minutos: '' as number | string,
  observacoes: '',
};

export default function ApontamentosPage() {
  const navigate = useNavigate();
  const [tabAtiva, setTabAtiva] = useState<'producao' | 'consumo'>('producao');
  const [apontamentosProducao, setApontamentosProducao] = useState<ApontamentoProducao[]>([]);
  const [apontamentosConsumo, setApontamentosConsumo] = useState<ApontamentoConsumo[]>([]);
  const [ordensProducao, setOrdensProducao] = useState<OrdemProducao[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [lotesDisponiveis, setLotesDisponiveis] = useState<Lote[]>([]);
  const [operacoesOP, setOperacoesOP] = useState<OrdemProducaoOperacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [filtroOP, setFiltroOP] = useState('');

  const [dialog, setDialog] = useState(false);
  const [formTipo, setFormTipo] = useState<'producao' | 'consumo'>('producao');
  const [form, setForm] = useState(FORM_VAZIO);

  const ordensProducaoAtivas = useMemo(
    () => ordensProducao.filter((o) => getUuidCandidate(o) && ['EM_PRODUCAO', 'PAUSADA'].includes(o.status)),
    [ordensProducao]
  );

  const ordensProducaoValidas = useMemo(
    () => ordensProducao.filter((o) => getUuidCandidate(o)),
    [ordensProducao]
  );

  const produtosMP = useMemo(
    () => produtos.filter((p) => getUuidCandidate(p) && !TIPOS_PRODUTO_ACABADO.includes(p.tipo_produto_codigo || '')),
    [produtos]
  );

  const lotesDisponiveisValidos = useMemo(
    () => lotesDisponiveis.filter((lote) => getUuidCandidate(lote)),
    [lotesDisponiveis]
  );

  const carregar = async () => {
    setLoading(true);
    try {
      const [producao, consumo] = await Promise.all([
        apontamentoService.producao.listar({ op: filtroOP || undefined }),
        apontamentoService.consumo.listar({ op: filtroOP || undefined }),
      ]);
      setApontamentosProducao(producao);
      setApontamentosConsumo(consumo);
    } catch (error) {
      console.error('Erro ao carregar apontamentos', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    ordemProducaoService.listar().then(setOrdensProducao).catch(console.error);
    produtoService.listar().then(setProdutos).catch(console.error);
  }, []);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroOP]);

  const abrirDialog = () => {
    setForm(FORM_VAZIO);
    setFormTipo('producao');
    setOperacoesOP([]);
    setLotesDisponiveis([]);
    setDialog(true);
  };

  const onOPChange = async (opId: string) => {
    setForm((f) => ({ ...f, op: opId, operacao: '' }));
    if (!opId) {
      setOperacoesOP([]);
      return;
    }
    try {
      const detalhe = await ordemProducaoService.buscarPorId(opId);
      setOperacoesOP(detalhe.operacoes || []);
    } catch (error) {
      console.error('Erro ao carregar operações da OP', error);
    }
  };

  const onProdutoChange = async (produtoId: string) => {
    setForm((f) => ({ ...f, produto: produtoId, lote: '' }));
    if (!produtoId) {
      setLotesDisponiveis([]);
      return;
    }
    try {
      const lotes = await loteService.listar({ produto: produtoId, status: 'LIVRE' });
      setLotesDisponiveis(lotes);
    } catch (error) {
      console.error('Erro ao carregar lotes do produto', error);
    }
  };

  const salvar = async () => {
    if (!isUuid(form.op) || !form.quantidade) return;
    setSalvando(true);
    try {
      if (formTipo === 'producao') {
        await apontamentoService.producao.registrar({
          op: form.op,
          operacao: form.operacao || null,
          quantidade_produzida: Number(form.quantidade),
          quantidade_refugo: Number(form.quantidade_refugo) || 0,
          funcionario: form.funcionario,
          tempo_real_minutos: form.tempo_real_minutos ? Number(form.tempo_real_minutos) : null,
          observacoes: form.observacoes,
        });
      } else {
        if (!isUuid(form.produto) || !form.responsavel) {
          setSalvando(false);
          return;
        }
        await apontamentoService.consumo.registrar({
          op: form.op,
          produto: form.produto,
          lote: form.lote || null,
          quantidade: Number(form.quantidade),
          responsavel: form.responsavel,
          operacao: form.operacao || null,
        });
      }
      setDialog(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao registrar apontamento', error);
    } finally {
      setSalvando(false);
    }
  };

  const deletarProducao = async (item: ApontamentoProducao) => {
    try {
      await apontamentoService.producao.estornar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao estornar apontamento de produção', error);
      throw error;
    }
  };

  const deletarConsumo = async (item: ApontamentoConsumo) => {
    try {
      await apontamentoService.consumo.estornar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao estornar apontamento de consumo', error);
      throw error;
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Timer size={21} strokeWidth={2} />}
        title="Apontamentos de Produção"
        description="Registro de apontamentos de produção e consumo de materiais das ordens de produção."
        actions={
          <Button type="button" onClick={() => navigate('/producao/apontamentos/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Novo Apontamento
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <Select filter value={filtroOP} onChange={(e) => setFiltroOP(e.target.value)}>
                <option value="">Todas as Ordens de Produção</option>
                {ordensProducaoValidas.map((o) => (
                  <option key={o.id} value={getUuidCandidate(o) || ''}>{o.numero} - {o.produto_nome}</option>
                ))}
              </Select>
            </div>
          }
        />

        <div className="nx-tabs">
          <button
            type="button"
            className={tabAtiva === 'producao' ? 'nx-tab nx-tab--active' : 'nx-tab'}
            onClick={() => setTabAtiva('producao')}
          >
            Apontamentos de Produção
          </button>
          <button
            type="button"
            className={tabAtiva === 'consumo' ? 'nx-tab nx-tab--active' : 'nx-tab'}
            onClick={() => setTabAtiva('consumo')}
          >
            Consumo de Materiais
          </button>
        </div>

        {tabAtiva === 'producao' ? (
          !loading && apontamentosProducao.length === 0 ? (
            <EmptyState message="Nenhum apontamento de produção encontrado." />
          ) : (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <thead>
                  <tr>
                    <th>OP</th>
                    <th>Operação</th>
                    <th>Data</th>
                    <th>Qtd. Produzida</th>
                    <th>Qtd. Refugo</th>
                    <th>Funcionário</th>
                    <th className="nx-col-acoes">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="nx-table-loading">Carregando…</td>
                    </tr>
                  ) : (
                    apontamentosProducao.map((item) => (
                      <tr key={item.id}>
                        <td>{item.op_numero}</td>
                        <td>{item.operacao_nome || '-'}</td>
                        <td>{formatDateTime(item.data_apontamento)}</td>
                        <td>{formatNumber(item.quantidade_produzida)}</td>
                        <td>{formatNumber(item.quantidade_refugo)}</td>
                        <td>{item.funcionario}</td>
                        <td className="nx-col-acoes">
                          <TableActions
                            onDelete={() => deletarProducao(item)}
                            deleteTitle="Estornar apontamento"
                            deleteDescription={`Deseja realmente estornar (excluir) o apontamento de produção da OP "${item.op_numero}"? Esta ação não poderá ser desfeita.`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )
        ) : !loading && apontamentosConsumo.length === 0 ? (
          <EmptyState message="Nenhum consumo de material encontrado." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>OP</th>
                  <th>Produto</th>
                  <th>Lote</th>
                  <th>Data</th>
                  <th>Quantidade</th>
                  <th>Responsável</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  apontamentosConsumo.map((item) => (
                    <tr key={item.id}>
                      <td>{item.op_numero}</td>
                      <td>{item.produto_nome} {item.produto_codigo ? `(${item.produto_codigo})` : ''}</td>
                      <td><Chip>{item.lote_numero || '-'}</Chip></td>
                      <td>{formatDateTime(item.data_consumo)}</td>
                      <td>{formatNumber(item.quantidade)}</td>
                      <td>{item.responsavel}</td>
                      <td className="nx-col-acoes">
                        <TableActions
                          onDelete={() => deletarConsumo(item)}
                          deleteTitle="Estornar consumo"
                          deleteDescription={`Deseja realmente estornar (excluir) o consumo do produto "${item.produto_nome}"? Esta ação não poderá ser desfeita.`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={(next) => !salvando && setDialog(next)}>
        <DialogContent className="nexus-dialog--wide nx-sngpc-edit-dialog">
          <form onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
          <DialogHeader>
            <DialogTitle>{formTipo === 'producao' ? 'Apontamento de Produção' : 'Consumo de Material'}</DialogTitle>
            <DialogDescription>
              {formTipo === 'producao'
                ? 'Registre a produção realizada para a operação da ordem de produção.'
                : 'Registre o consumo de material para a ordem de produção.'}
            </DialogDescription>
          </DialogHeader>

          <div className="nexus-dialog-body">
            <div className="nx-tabs">
              <button
                type="button"
                className={formTipo === 'producao' ? 'nx-tab nx-tab--active' : 'nx-tab'}
                onClick={() => setFormTipo('producao')}
              >
                Produção
              </button>
              <button
                type="button"
                className={formTipo === 'consumo' ? 'nx-tab nx-tab--active' : 'nx-tab'}
                onClick={() => setFormTipo('consumo')}
              >
                Consumo
              </button>
            </div>

            <div className="nx-form-grid">
              <Field label="Ordem de Produção *" htmlFor="apontamento-op" className="nx-sngpc-field--full">
                <Select id="apontamento-op" value={form.op} onChange={(e) => onOPChange(e.target.value)}>
                  <option value="">Selecione</option>
                  {ordensProducaoAtivas.map((o) => (
                    <option key={o.id} value={getUuidCandidate(o) || ''}>{o.numero} - {o.produto_nome}</option>
                  ))}
                </Select>
              </Field>

              {formTipo === 'producao' && (
                <Field label="Operação" htmlFor="apontamento-operacao" className="nx-sngpc-field--full">
                  <Select
                    id="apontamento-operacao"
                    value={form.operacao}
                    onChange={(e) => setForm({ ...form, operacao: e.target.value })}
                  >
                    <option value="">-</option>
                    {operacoesOP.map((op) => (
                      <option key={op.id} value={op.id}>{op.sequencia} - {op.operacao}</option>
                    ))}
                  </Select>
                </Field>
              )}

              {formTipo === 'consumo' && (
                <>
                  <Field label="Produto *" htmlFor="apontamento-produto">
                    <Select id="apontamento-produto" value={form.produto} onChange={(e) => onProdutoChange(e.target.value)}>
                      <option value="">Selecione</option>
                      {produtosMP.map((p) => (
                        <option key={p.id} value={getUuidCandidate(p) || ''}>{p.codigo} - {p.descricao}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Lote" htmlFor="apontamento-lote">
                    <Select id="apontamento-lote" value={form.lote} onChange={(e) => setForm({ ...form, lote: e.target.value })}>
                      <option value="">-</option>
                      {lotesDisponiveisValidos.map((l) => (
                        <option key={l.id} value={getUuidCandidate(l) || ''}>{l.numero_lote} ({formatNumber(l.quantidade)})</option>
                      ))}
                    </Select>
                  </Field>
                </>
              )}

              <Field label={formTipo === 'producao' ? 'Qtd. Produzida *' : 'Quantidade *'} htmlFor="apontamento-quantidade">
                <DecimalInput
                  id="apontamento-quantidade"
                  value={form.quantidade}
                  onValueChange={(value) => setForm({ ...form, quantidade: value ?? 0 })}
                />
              </Field>

              {formTipo === 'producao' ? (
                <>
                  <Field label="Qtd. Refugo" htmlFor="apontamento-refugo">
                    <DecimalInput
                      id="apontamento-refugo"
                      value={form.quantidade_refugo}
                      onValueChange={(value) => setForm({ ...form, quantidade_refugo: value ?? 0 })}
                    />
                  </Field>
                  <Field label="Funcionário *" htmlFor="apontamento-funcionario">
                    <Input
                      id="apontamento-funcionario"
                      value={form.funcionario}
                      onChange={(e) => setForm({ ...form, funcionario: e.target.value })}
                    />
                  </Field>
                  <Field label="Tempo Real (min)" htmlFor="apontamento-tempo">
                    <DecimalInput
                      id="apontamento-tempo"
                      value={form.tempo_real_minutos}
                      onValueChange={(value) => setForm({ ...form, tempo_real_minutos: value ?? '' })}
                    />
                  </Field>
                  <Field label="Observações" htmlFor="apontamento-observacoes" className="nx-sngpc-field--full">
                    <Textarea
                      id="apontamento-observacoes"
                      value={form.observacoes}
                      onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                      rows={2}
                    />
                  </Field>
                </>
              ) : (
                <Field label="Responsável *" htmlFor="apontamento-responsavel">
                  <Input
                    id="apontamento-responsavel"
                    value={form.responsavel}
                    onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
                  />
                </Field>
              )}
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={salvando}>Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={salvando} loadingText="Salvando...">
              <Save size={16} /> Salvar
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
